/** Trainer interface (SPEC §4.1). */
export type TrainerData = {
  t: number; // ms timestamp
  powerW?: number;
  cadenceRpm?: number;
  speedKmh?: number; // trainer-reported, if any
  hrBpm?: number;
};

export type SimParams = { gradePct: number; crr: number; cwKgPerM: number; windMps: number };

export type TrainerKind = 'ftms' | 'fec' | 'wahoo-legacy' | 'sim';

export interface TrainerCaps {
  erg: boolean;
  sim: boolean;
  powerRange?: [number, number];
}

export interface Trainer {
  readonly kind: TrainerKind;
  readonly name: string;
  connect(): Promise<void>;
  disconnect(): Promise<void>;
  onData(cb: (d: TrainerData) => void): () => void;
  onConnectionChange(cb: (connected: boolean) => void): () => void;
  setErg(watts: number): Promise<void>;
  setSim(p: SimParams): Promise<void>;
  readonly caps: { erg: boolean; sim: boolean; powerRange?: [number, number] };
}

/** Optional extras some implementations provide (used by UI / debug). */
export interface TrainerExtras {
  readonly connected: boolean;
  readonly deviceId: string;
  /** Log lines for the debug overlay. */
  onLog?(cb: (line: string) => void): () => void;
  /** Rider weight etc. for trainers that take user config (FE-C page 55, Wahoo sim init). */
  setUserConfig?(cfg: { riderKg: number; bikeKg: number; wheelCircumferenceM: number }): Promise<void>;
}

export type AnyTrainer = Trainer & TrainerExtras;

/** Simple typed event emitter used by device classes. */
export class Emitter<T> {
  private readonly listeners = new Set<(v: T) => void>();
  on(cb: (v: T) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }
  emit(v: T): void {
    for (const l of this.listeners) {
      try {
        l(v);
      } catch (err) {
        console.error(err);
      }
    }
  }
  get size(): number {
    return this.listeners.size;
  }
}
